import { Maximize2, Minus, Plus } from "lucide-react"
import { useState } from "react"

import { Boton } from "@/componentes/ui/Boton"

import { TarjetaPosicion } from "./TarjetaPosicion"
import { type Arbol, armarBosque, type Nodo } from "./tipos"

/**
 * El organigrama dibujado: árboles de arriba abajo, con zoom.
 *
 * ⚠ **Las líneas son cajas con borde, no un SVG superpuesto.** Un SVG de conectores
 * hay que recalcularlo con las posiciones reales de cada tarjeta después de pintar, y
 * cualquier cambio de tipografía o de zoom lo descuadra. Con bordes, el navegador
 * coloca las líneas al maquetar: no hay nada que sincronizar y la impresión sale bien.
 *
 * ⚠ **El zoom escala con `transform`, no cambiando tamaños.** Es lo único que se puede
 * animar sin obligar al navegador a recalcular la maqueta entera en cada paso, y aquí
 * hay decenas de tarjetas.
 *
 * ⚠ **Arrastrar tiene alternativa de un solo puntero** (WCAG 2.5.7): el panel de la
 * posición trae un selector «Depende de». Arrastrar es el atajo, no la única vía.
 */

const PASOS_ZOOM = [0.5, 0.65, 0.8, 1, 1.2, 1.5]

function Rama({
  nodo,
  seleccionada,
  arrastrable,
  alElegir,
  alSoltar,
}: {
  nodo: Arbol
  seleccionada: number | null
  arrastrable: boolean
  alElegir: (id: number) => void
  alSoltar?: (arrastrado: number, destino: number) => void
}) {
  const conHijos = nodo.hijos.length > 0
  return (
    <li className="flex flex-col items-center">
      <TarjetaPosicion
        nodo={nodo}
        seleccionada={seleccionada === nodo.id}
        arrastrable={arrastrable}
        alElegir={alElegir}
        alSoltar={alSoltar}
      />
      {conHijos && (
        <>
          {/* El tallo que baja de la tarjeta hacia la barra de sus hijos. */}
          <span className="h-5 w-px" style={{ backgroundColor: "var(--border)" }} />
          <ul className="flex items-start">
            {nodo.hijos.map((hijo, i) => (
              <li key={hijo.id} className="relative flex flex-col items-center px-3">
                {/* La barra horizontal: media a cada lado, y en los extremos sólo la
                    mitad que mira hacia dentro. Así una fila de tres hijos dibuja
                    una barra continua sin sobresalir por los bordes. */}
                <span
                  className="absolute top-0 h-px"
                  style={{
                    backgroundColor: "var(--border)",
                    left: i === 0 ? "50%" : 0,
                    right: i === nodo.hijos.length - 1 ? "50%" : 0,
                    display: nodo.hijos.length === 1 ? "none" : undefined,
                  }}
                />
                <span className="h-5 w-px" style={{ backgroundColor: "var(--border)" }} />
                <ul>
                  <Rama
                    nodo={hijo}
                    seleccionada={seleccionada}
                    arrastrable={arrastrable}
                    alElegir={alElegir}
                    alSoltar={alSoltar}
                  />
                </ul>
              </li>
            ))}
          </ul>
        </>
      )}
    </li>
  )
}

export function LienzoOrganigrama({
  nodos,
  seleccionada,
  arrastrable,
  alElegir,
  alSoltar,
}: {
  nodos: Nodo[]
  seleccionada: number | null
  arrastrable: boolean
  alElegir: (id: number) => void
  alSoltar?: (arrastrado: number, destino: number) => void
}) {
  const [paso, setPaso] = useState(3)
  const escala = PASOS_ZOOM[paso] ?? 1
  const raices = armarBosque(nodos)

  if (raices.length === 0) {
    return (
      <p className="py-16 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Este organigrama todavía no tiene posiciones.
      </p>
    )
  }

  return (
    <div>
      <div className="no-print mb-3 flex items-center gap-1">
        <Boton
          variante="fantasma"
          tamano="sm"
          onClick={() => setPaso((p) => Math.max(0, p - 1))}
          disabled={paso === 0}
          aria-label="Alejar"
        >
          <Minus size={14} aria-hidden="true" />
        </Boton>
        <span className="w-12 text-center text-xs tabular" style={{ color: "var(--fg-muted)" }}>
          {Math.round(escala * 100)} %
        </span>
        <Boton
          variante="fantasma"
          tamano="sm"
          onClick={() => setPaso((p) => Math.min(PASOS_ZOOM.length - 1, p + 1))}
          disabled={paso === PASOS_ZOOM.length - 1}
          aria-label="Acercar"
        >
          <Plus size={14} aria-hidden="true" />
        </Boton>
        <Boton variante="fantasma" tamano="sm" onClick={() => setPaso(3)} aria-label="Tamaño real">
          <Maximize2 size={14} aria-hidden="true" />
        </Boton>
      </div>

      <div className="overflow-auto">
        <div
          style={{
            transform: `scale(${escala})`,
            transformOrigin: "top left",
            // Sin esto, al reducir queda un hueco enorme abajo: el contenedor
            // reserva el tamaño sin escalar.
            width: `${100 / escala}%`,
          }}
        >
          <ul className="flex items-start justify-center gap-8 py-4">
            {raices.map((raiz) => (
              <Rama
                key={raiz.id}
                nodo={raiz}
                seleccionada={seleccionada}
                arrastrable={arrastrable}
                alElegir={alElegir}
                alSoltar={alSoltar}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
